/**
 * 67.38: weather you hear coming (lane A's 62.54 step 5: rain, storms, dust and fog banks are cells that drift across the
 * planet, core/windField.ts cellsAt). Before a cell arrives: its rain's hiss swells from its side as its edge nears and
 * thins as it leaves, a dust front's grit rises ahead of it, and a storm cell's thunder rolls from its direction, late by
 * its distance and duller far off. Overhead, the weather at the listener plays as before (WeatherSound.set). Pure: the
 * cues from the cells and the listener's place; WeatherSound plays them (`ahead`).
 */
import type { Vec3 } from '../types';
import type { WindCell } from '../core/windField';

/** How far ahead of its edge a cell is heard (radians of the planet: a rain cell drifts this far in about half a minute). */
export const AHEAD_RAD = 0.35;
/** How far from its edge a storm's thunder still carries (radians). */
export const THUNDER_RAD = 0.8;

export interface WeatherCues {
  /** a rain or storm cell's hiss ahead of its edge: 0 … 1, and its side (−1 left … 1 right) */
  rain: { level: number; pan: number };
  /** a dust or ash front's grit ahead of it */
  grit: { level: number; pan: number };
  /** the nearest storm cell outside, when its thunder carries: its distance past the edge (radians) and its side */
  storm: { rad: number; pan: number } | null;
}

export const NO_CUES: WeatherCues = { rain: { level: 0, pan: 0 }, grit: { level: 0, pan: 0 }, storm: null };

const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;

/**
 * The cues at a listener standing along `up` (unit, from the planet's centre), with `right` the listener's right (a world
 * direction across the view): for each cell, how far its edge is (the angle to its centre less its radius) and which side
 * it lies. A cell the listener is inside plays as the weather overhead, not ahead. Pure.
 */
export function weatherCues(cells: readonly WindCell[], up: Vec3, right: Vec3): WeatherCues {
  const out: WeatherCues = { rain: { level: 0, pan: 0 }, grit: { level: 0, pan: 0 }, storm: null };
  const n = Math.hypot(up.x, up.y, up.z) || 1, u = { x: up.x / n, y: up.y / n, z: up.z / n };
  for (const c of cells) {
    const cosA = Math.max(-1, Math.min(1, dot(u, c.centre)));
    const edge = Math.acos(cosA) - c.radius;
    if (edge <= 0) continue;   // overhead: the weather here plays it
    // its side: the way to its centre along the ground, against the listener's right
    const t = { x: c.centre.x - u.x * cosA, y: c.centre.y - u.y * cosA, z: c.centre.z - u.z * cosA };
    const tl = Math.hypot(t.x, t.y, t.z);
    const pan = tl > 1e-9 ? Math.max(-1, Math.min(1, dot(t, right) / tl)) : 0;
    const near = Math.max(0, 1 - edge / AHEAD_RAD);
    const ahead = near * near;   // swelling as the edge nears
    if ((c.kind === 'rain' || c.kind === 'storm') && ahead > out.rain.level) out.rain = { level: ahead, pan };
    if ((c.kind === 'dust' || c.kind === 'ash') && ahead > out.grit.level) out.grit = { level: ahead, pan };
    if (c.kind === 'storm' && edge < THUNDER_RAD && (!out.storm || edge < out.storm.rad)) out.storm = { rad: edge, pan };
  }
  return out;
}

/**
 * A distant storm's thunder: how late (seconds: sound's travel, capped so a roll still belongs to its flash), how dull
 * (the low-pass's top, Hz) and how loud (a share of the overhead roll's) at `rad` past the edge, on a planet whose radius
 * is `radiusMetres`. Pure.
 */
export function distantThunder(rad: number, radiusMetres: number): { delay: number; cutoff: number; level: number } {
  const metres = Math.max(0, rad) * radiusMetres;
  const k = Math.max(0, Math.min(1, rad / THUNDER_RAD));
  return { delay: Math.min(8, metres / 343), cutoff: 320 * (1 - 0.6 * k) + 60, level: 0.6 * (1 - 0.8 * k) };
}

/** 67.38: in a fog bank, distant gunfire muffled — duller and softer past a few people (`people` the sound's distance). */
export function fogMuffle<T extends { gain: number; cutoff: number }>(h: T, people: number): T {
  const far = Math.max(0, people - 8);
  const k = Math.exp(-far / 30);
  return { ...h, cutoff: h.cutoff * (0.25 + 0.75 * k), gain: h.gain * (0.6 + 0.4 * k) };
}
